import React, { useState, useMemo, useEffect } from 'react';
import { createOffering, updateOffering, getModalities, getProfile } from '../api/client';
import ModalitySelector, { computeInitialOpenGroups } from './ModalitySelector';

// Shared "create/edit an Offering" modal — reused from Settings' Offerings
// card, the onboarding wizard's Offerings step, and a "+ New offering type"
// entry point inside SessionForm. Always talks to the API directly
// (createOffering/updateOffering) and hands the saved row back via onSaved,
// so every caller gets identical persistence behavior for free.
//
// Deliberately has no description field (unlike the underlying data model,
// which still supports one) — matches the design this was built from.
// Editing an offering that already has a description leaves it untouched,
// since the update payload only ever includes name/format/modalities.
//
// modalityGroups/defaultModalities are optional: callers that already have
// them loaded (Settings, onboarding) pass them in; anywhere else (SessionForm,
// Clients) the modal fetches them itself the first time it opens.
export default function OfferingFormModal({ open, onClose, onSaved, existingOffering, modalityGroups: modalityGroupsProp, defaultModalities: defaultModalitiesProp }) {
  const isEdit = !!existingOffering;
  const needsFetch = !modalityGroupsProp || !defaultModalitiesProp;
  const [fetched, setFetched] = useState(null);
  const modalityGroups = modalityGroupsProp || fetched?.modalityGroups || [];
  const defaultModalities = defaultModalitiesProp || fetched?.defaultModalities || [];
  const ready = !needsFetch || !!fetched;

  useEffect(() => {
    if (!open || !needsFetch || fetched) return;
    Promise.all([getModalities(), getProfile()])
      .then(([modalitiesRes, profileRes]) => {
        const profile = profileRes.data;
        setFetched({
          modalityGroups: modalitiesRes.data || [],
          defaultModalities: profile?.modalities?.length ? profile.modalities : (profile?.modality ? [profile.modality] : []),
        });
      })
      .catch(() => setFetched({ modalityGroups: [], defaultModalities: [] }));
  }, [open, needsFetch, fetched]);

  const [name, setName] = useState('');
  const [format, setFormat] = useState('individual');
  const [modalities, setModalities] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  const [showAllModalities, setShowAllModalities] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // The component instance persists across opens (callers just toggle `open`
  // rather than mounting/unmounting it), so state must be explicitly reset
  // here each time it opens — otherwise a second "create" after an earlier
  // save/edit would start from stale leftover values instead of a fresh form.
  useEffect(() => {
    if (!open || !ready) return;
    const initialModalities = existingOffering ? (existingOffering.modalities || []) : [...defaultModalities];
    setName(existingOffering?.name || '');
    setFormat(existingOffering?.format || 'individual');
    setModalities(initialModalities);
    setOpenGroups(computeInitialOpenGroups(modalityGroups, initialModalities));
    setShowAllModalities(false);
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingOffering, ready]);

  // Same default-list-plus-escape-hatch pattern used everywhere else Offering
  // modalities are picked: default to the workspace's own modality palette,
  // with a link to reveal the full canonical list for anything not in it.
  const pickerGroups = useMemo(() => {
    if (showAllModalities) return modalityGroups;
    const allowed = new Set([...defaultModalities, ...modalities]);
    return modalityGroups
      .map(g => ({ ...g, modalities: g.modalities.filter(m => allowed.has(m.name)) }))
      .filter(g => g.modalities.length > 0);
  }, [modalityGroups, defaultModalities, modalities, showAllModalities]);

  if (!open) return null;

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const payload = { name: name.trim(), format, modalities };
      const res = isEdit ? await updateOffering(existingOffering.id, payload) : await createOffering(payload);
      onSaved(res.data);
    } catch (err) {
      setError('Could not save offering.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.title}>{isEdit ? 'Edit offering type' : 'New offering type'}</div>
          <button style={styles.close} onClick={onClose}>×</button>
        </div>
        <div style={styles.subtitle}>Same form in onboarding and when you add a session.</div>

        <div style={styles.field}>
          <label style={styles.label}>Title</label>
          <input
            style={styles.input}
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. 1:1 Executive Coaching"
            autoFocus
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Format</label>
          <div style={styles.formatRow}>
            <button
              type="button"
              style={{ ...styles.formatOption, ...(format === 'individual' ? styles.formatOptionActive : {}) }}
              onClick={() => setFormat('individual')}
            >
              1:1
            </button>
            <button
              type="button"
              style={{ ...styles.formatOption, ...(format === 'group' ? styles.formatOptionActive : {}) }}
              onClick={() => setFormat('group')}
            >
              Group
            </button>
          </div>
          <div style={styles.hint}>Group = 2 or more people.</div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Modalities</label>
          <div style={styles.hint}>Starts from your workspace picks. Tweak just for this offering.</div>
          <ModalitySelector
            modalityGroups={pickerGroups}
            selected={modalities}
            onToggle={m => setModalities(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m])}
            openGroups={openGroups}
            onToggleGroup={g => setOpenGroups(prev => ({ ...prev, [g]: !prev[g] }))}
          />
          {!showAllModalities && (
            <button type="button" style={styles.addModalityLink} onClick={() => setShowAllModalities(true)}>
              + Add a modality not in your list
            </button>
          )}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.footer}>
          <button style={styles.ghostBtn} onClick={onClose}>Cancel</button>
          <button style={styles.saveBtn} onClick={handleSave} disabled={!name.trim() || saving}>
            {saving ? 'Saving...' : isEdit ? 'Save changes' : 'Create offering type'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(26,46,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(2px)', padding: '20px' },
  modal: { background: 'white', borderRadius: '16px', width: '480px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)', padding: '28px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' },
  title: { fontFamily: "'Lora', Georgia, serif", fontSize: '22px', fontWeight: '400', color: 'var(--text-dark)' },
  close: { background: 'none', border: 'none', fontSize: '24px', color: 'var(--text-faint)', cursor: 'pointer', lineHeight: 1 },
  subtitle: { fontSize: '13px', color: 'var(--text-soft)', marginBottom: '22px' },
  field: { marginBottom: '20px' },
  label: { display: 'block', fontSize: '11.5px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-soft)', marginBottom: '8px' },
  input: { width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box' },
  hint: { fontSize: '12px', color: 'var(--text-faint)', marginTop: '6px', lineHeight: 1.5 },
  formatRow: { display: 'flex', gap: '8px' },
  formatOption: { flex: 1, padding: '11px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  formatOptionActive: { border: '1px solid var(--sage)', background: 'var(--mint)', color: 'var(--sage)' },
  addModalityLink: { background: 'none', border: 'none', color: 'var(--sage)', fontFamily: "'DM Sans', sans-serif", fontSize: '12.5px', cursor: 'pointer', padding: '8px 0 0' },
  error: { fontSize: '13px', color: 'var(--red)', marginBottom: '12px' },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' },
  saveBtn: { padding: '11px 22px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  ghostBtn: { padding: '10px 16px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer' },
};
